import { MutableRefObject, useEffect, useRef, useState } from "react"
import { motion } from "framer-motion"
import { BsXCircle } from "react-icons/bs";
import { BsCheckCircle } from "react-icons/bs";
import { BsQuestionCircle } from "react-icons/bs";
import './HexPatternPositions.scss'
import { produce } from "immer"
import { IntervalLines } from "../../../Comps/Connectors/IntervalLines";
import { HxMapGroup } from "../../../Comps/HxMap/HxMapGroup";
import { HexMap } from "../../../Data/HexMap/HexMap";
import { HexMapBuilderParams, BuildHexMap } from "../../../Data/HexMap/HexMapBuilder";
import { HexMapModifier } from "../../../Data/HexMap/HexMapModifier";
import { Note } from "../../../Data/HexMap/Note";
import { ClassNames } from "../../../Util/ClassNames";
import { All } from "../../../Util/Constants/Quantities";
import { TransformTranslate } from "../../../Util/Transform/Transform";
import { PositionSelector } from "../../../Util/PositionSelector/PositionSelector";
import { OutlineBox } from "../../../Comps/OutlineBox/OutlineBox";
import { ExerciseMessage } from "../Utils/Message/ExerciseMessage";
import { Music } from "../../../Util/Music/Music";
import { useNotePlayer } from "../../../Sound/SoundStore";
import { NotePlayer } from "../../../Sound/NotePlayer";

export function HexPatternSequence(){
    const notePlayer:NotePlayer = useNotePlayer()
    const maxSelectionHistory:number = 4
    const sequenceCount:number = 7
    const radius:number = 130
    const hexWidth:number = radius * 2.8
    const hexHeight:number = radius * 2.8
    const getSequenceAnswers = (initialPosition:number) => {
        const answers:number[] = []
        for(let i = 1; i < sequenceCount; i++){
            answers.push((initialPosition + i) % 7)
        }
        return answers
    }
    const positionSelector:MutableRefObject<PositionSelector> = useRef(new PositionSelector(maxSelectionHistory))
    const selectedPosition:MutableRefObject<number> = useRef(positionSelector.current.getRandomPosition(-1))
    const sequenceAnswers:MutableRefObject<number[]> = useRef(getSequenceAnswers(selectedPosition.current))
    
    const answerIs:MutableRefObject<boolean|null> =  useRef(null)
    const sequenceComplete:MutableRefObject<boolean> =  useRef(false)
    const getMessage = () => {
        if(answerIs.current === null){
            return `Where is the ${selectedPosition.current + 1} note`
        }
        if(answerIs.current === true){
            return `Correct`
        }
        return `Incorrect`
    }
    const getInitialMessage = () => {
        return `Starting with the ${selectedPosition.current + 1} note, select the other notes in sequence.`
    }
    const [message, setMessage] = useState(getInitialMessage())
    const onSequenceComplete = () => {
        notePlayer.reset()
        answerIs.current = null
        selectedPosition.current = positionSelector.current.getRandomPosition()
        sequenceAnswers.current = getSequenceAnswers(selectedPosition.current)
        setMap(getMap())
        setMessage(getInitialMessage())
    }
    const onCorrect = (note:Note) => {
        notePlayer.playNote(note.params.tonic)
        answerIs.current = true
        const correctIndex:number = sequenceAnswers.current[0]
        sequenceAnswers.current.splice(0,1)
        setMap(produce((draft)=>{
            draft.notes[correctIndex].selectedState = Note.SelectedState_Selected
        }))
        if(sequenceAnswers.current.length === 0){
            setMessage('Sequence Complete.')
            setTimeout(onSequenceComplete, 1000)
        }
        else{
            setMessage(`Correct. Now select the rest of the notes`)
        }
    }
    const onIncorrect = () => {
        notePlayer.playBuzzer()
        answerIs.current = false
        setMessage('Incorrect')
        setTimeout(()=>{
            answerIs.current = null; 
            let remainingString:string = (sequenceAnswers.current.length > 1) ? `the next ${sequenceAnswers.current.length} notes` : 'the last note'
            if(sequenceAnswers.current.length === sequenceCount){
                remainingString = `the ${selectedPosition.current + 1} note and ` + remainingString
            }
            setMessage(`Select ${remainingString}`)
        }, 1500)
    }
    const onNoteClick = (note:Note) => {
        if(note.params.noteIndex === sequenceAnswers.current[0]){onCorrect(note)}
        else{ onIncorrect()}
    }
    const onKeyClick = () => {}
    const getMap = () => {
        const params:HexMapBuilderParams = {
            rows:1, columns:1, tonic:Music.G, radius:radius
        }
        const map:HexMap = BuildHexMap(params)
        const mod:HexMapModifier = new HexMapModifier(map)
        mod.notes_setDefined([All], false)
        mod.notes_setSelected([selectedPosition.current])
        return map
    }
    const [map, setMap] = useState(getMap())
    const mod:MutableRefObject<HexMapModifier> = useRef(new HexMapModifier(map))
    const update = () => {
        setMessage(getMessage())
        setMap(produce((draft)=>{
            mod.current.setDraft(draft)
            mod.current.notes_setDefined([All], false)
            mod.current.notes_setSelectedState([All], Note.SelectedState_Default)
            if(answerIs.current === true){
                mod.current.notes_setDefined([selectedPosition.current], true)
                mod.current.notes_setSelectedState([selectedPosition.current], Note.SelectedState_Hilited)
            }
        }))
    }
    const lines:MutableRefObject<JSX.Element> = useRef(<IntervalLines notes={map.notes} displayConfig={map.params.displayConfig}/>)

    useEffect(()=>{
        notePlayer.reset()
    },[])
    return(
        <OutlineBox title={'The HexPattern Sequence Exercise'} width={600}>
            <div className={'hex-pattern'}>
                <ExerciseMessage key={Date.now()} message={message} answerStatus={answerIs.current}/>
                <div>
                    <svg width={hexWidth} height={hexHeight}>
                        <g transform={TransformTranslate(hexWidth/2,hexHeight/2)}>
                            <HxMapGroup onNoteClick={onNoteClick} onKeyClick={onKeyClick} hexMap={map}>
                                {lines.current}
                            </HxMapGroup>
                        </g>
                    </svg>
                </div>
            </div>
        </OutlineBox>
        
    )
}
function Message(props:{message:string, correct:boolean|null}){
    const scale:number = 0
    const animateScale:number = 1
    const getClassNames = () => {
        const classNames:ClassNames = new ClassNames(['exercise-message'])
        if(props.correct !== null){ classNames.addClass((props.correct) ? 'correct' : 'incorrect') }
        return classNames.toString()
    }
    const getIcon = ():React.ReactNode => {
        if(props.correct === null){return <BsQuestionCircle/>}
        return(props.correct) ? <BsCheckCircle/> : <BsXCircle />
    }
    return(
        <div className='centered'>
            <motion.div initial={{scale:scale}} animate={{scale:animateScale}} transition={{duration:0.4}} className={getClassNames()}>
                <div className='container'><span>{props.message}</span>{getIcon()}</div>
            </motion.div>
        </div> 
    )
}